interface MetricCardProps {
  label: string;
  value: string | number;
  hint?: string;
  variant?: 'default' | 'highlight' | 'green';
  className?: string;
}

/**
 * Luxury MetricCard component with dark charcoal background and hover effects.
 *
 * Features:
 * - Dark charcoal background (bg-charcoal)
 * - Hover effect (translateY(-2px), border-color gold)
 * - JetBrains Mono for value (font-mono)
 * - Uppercase label styling (text-xs uppercase tracking-wider)
 * - Highlight variant shows gold border and gradient
 * - Green variant shows emerald border and gradient
 */
export function MetricCard({
  label,
  value,
  hint,
  variant = 'default',
  className = '',
}: MetricCardProps) {
  const variantStyles = {
    default: 'bg-charcoal border-slate-dark hover:border-gold',
    highlight: 'bg-gradient-to-br from-gold/15 to-gold/5 border-gold',
    green: 'bg-gradient-to-br from-emerald/15 to-emerald/5 border-emerald',
  };

  const valueStyles = {
    default: 'text-ivory',
    highlight: 'text-gold',
    green: 'text-emerald',
  };

  return (
    <div
      className={`
        border rounded-lg p-5 relative transition-all duration-300 ease-out shadow-sm
        hover:-translate-y-0.5 hover:border-gold hover:shadow-md
        ${variantStyles[variant]}
        ${className}
      `}
    >
      <div className="text-sm uppercase tracking-[0.15em] text-stone-500 font-semibold">
        {label}
      </div>
      <div
        className={`
          text-[26px] font-bold font-mono mt-2
          ${valueStyles[variant]}
        `}
      >
        {value}
      </div>
      {hint && (
        <div className="text-sm text-stone-400 mt-1.5">
          {hint}
        </div>
      )}
    </div>
  );
}
